import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { RouteLocationNamedRaw } from 'vue-router'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { createPlaygamaTopCategory } from '@/pages/Homepage/components/GamesBlock/TopPlaygama/prepare-top-playgama'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { ShortCategory } from '@/query/use-general-data'
import { useAppStore } from '@/store/app-store'
import { APICategoryData, MainCategoriesType, ShortGameZipType } from '@/types'
import { shuffle, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { HOMEPAGE_CATEGORIES_CACHE } from '@/utils/requests/utils'

export type MainCategoryListZipType = Omit<ShortCategory, 'location'> & {
    location: RouteLocationNamedRaw | undefined
    games?: ShortGameZipType[]
}

export const useHomepageData = () => {
    const appStore = useAppStore()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()
    const { getMainPageGamesByCategories } = useAPI()

    // persister type depends on queryKey cardinality
    const queryKey = computed(() => [
        'homepage-data',
        appStore.platform,
        currentLocale.value,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.isDebugMode,
        appStore.contentScope,
    ])
    const persister = usePersisterFn(Persisters.LowCardinality)

    const highlightedHomepageCategories = [
        'trending_now',
        'soccer',
        'top_growth',
        'top_playgama',
        'new',
        'casual-games',
    ]

    function mapCategoriesWithGames(categories: APICategoryData[]): MainCategoryListZipType[] {
        const result = categories
            .map(({ id, hru, title, games, categoryType }) => ({
                id: id as MainCategoriesType,
                title,
                location: getCategoryRoute({ hru, type: categoryType }),
                games: id === 'trending_now' ? shuffle(games.map(zipAPIGame)) : games.map(zipAPIGame),
            }))
            .filter(({ games }) => games.length > 0)

        return [...result, createPlaygamaTopCategory(appStore.isPurchaseEnabled)]
    }
    const { data, suspense } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await getMainPageGamesByCategories({
                platform: appStore.platform,
                locale: currentLocale.value,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                withDebugInfo: appStore.isDebugMode,
                scope: appStore.contentScope,
            })
            if (result.status !== 'ok') {
                throw new Error(result.status === 'error' ? result.message : `HTTP ${result.httpStatus}`)
            }

            return mapCategoriesWithGames(result.data.categories)
        },
        staleTime: HOMEPAGE_CATEGORIES_CACHE,
        persister,
    })

    return {
        categoriesWithGames: data,
        suspense,
        highlightedHomepageCategories,
    }
}
